import { FC } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  IconButton,
  RyeconClose,
  RyeconCode,
  RyeconCompass,
  RyeconShare,
  Sizes,
} from '@dbt-labs/sourdough';

import { ResourceChip } from '@/components/ResourceChip';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useAccountProjects } from '@/hooks/discovery/accountProjects';
import { useLinkToDbtCloud, useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { safeParseUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { sanitizeCoreFilePath } from '@/util/file';

type PanelHeaderProps = {
  nodeUniqueId: string;
  column: string;
  files: (string | null | undefined)[];
  environmentId?: number;
  projectId?: number;
};

export const ColumnPanelHeader: FC<PanelHeaderProps> = ({
  nodeUniqueId,
  column,
  files,
  projectId,
  environmentId,
}) => {
  const projectScope = useUrlProjectParams();
  const setSearchParams = useSearchParams()[1];
  const linkToResource = useLinkToExplorer('resourceDetails');
  const navigate = useNavigate();
  const { hasDeveloperLicense } = useDbtCloudAccount();
  const [accountProjectsQuery] = useAccountProjects({
    accountId: projectScope.accountId,
  });
  const getTargetEnvironmentType = useTargetEnvironmentType();

  const parsedUniqueId = safeParseUniqueId(nodeUniqueId).ok;
  const packageName = parsedUniqueId?.packageName;
  const currentProjectId = projectScope.projectId ?? 0;
  const coreProjectName = accountProjectsQuery.data?.account?.projects?.find(
    (x) => x.projectId === currentProjectId,
  )?.coreProjectName;

  const ideLink = useLinkToDbtCloud('cloudIde');

  const tooltip = 'View Column';

  return (
    <div className="flex w-full items-center border-b border-borderMuted p-4">
      <span className="flex-1">
        <ResourceChip resourceType="column" />
      </span>
      <span className="flex-0 flex items-center gap-2">
        <IconButton
          size={Sizes.lg}
          ryecon={RyeconCompass}
          className="size-6 align-middle"
          label={tooltip}
          tooltip={tooltip}
          isDisabled={!nodeUniqueId || !column}
          onClick={() => {
            const columnUrl = linkToResource({
              ...projectScope,
              dbtUniqueId: nodeUniqueId,
              tab: 'columns',
              originColumn: column,
              projectId: projectId || currentProjectId,
              environmentType: getTargetEnvironmentType(environmentId || 0),
            });
            navigate(columnUrl);
          }}
        />

        {ideLink && (
          <IconButton
            size={Sizes.lg}
            ryecon={RyeconCode}
            label="Open in IDE"
            tooltip="Open in IDE"
            className="size-6 align-middle"
            isDisabled={
              !projectId ||
              !hasDeveloperLicense ||
              !files.length ||
              !!(packageName && packageName !== coreProjectName)
            }
            onClick={() => {
              if (!projectId) {
                return false;
              }

              // TODO: review what properties and IDE link requires
              window.open(
                ideLink({
                  ...projectScope,
                  projectId: projectId,
                  file: files
                    .map((file) => sanitizeCoreFilePath(file, coreProjectName))
                    .filter(truthy),
                }),
                'cloud-ide', // re-use the same window/tab if opening multiple files
              );
            }}
          />
        )}

        <IconButton
          size={Sizes.lg}
          ryecon={RyeconShare}
          label="Copy Link to Page"
          tooltip="Copy Link to Page"
          className="size-6 align-middle"
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
            toast.success('Copied link to clipboard', { id: 'copy-link' });
          }}
        />

        <IconButton
          size={Sizes.lg}
          ryecon={RyeconClose}
          className="size-6 align-middle"
          label="Close Panel"
          tooltip="Close Panel"
          testId="close-column-panel-button"
          onClick={() => {
            setSearchParams((params) => {
              params.delete('selectedColumn');
              return params;
            });
          }}
        />
      </span>
    </div>
  );
};
